<template>
    <div class="n2-center-container" style="background-color: #fff; padding: 10px;">
      <div>
        <a @click="$router.go(-1)" class="n2-link-back">
          <div style="display: flex; align-items:center;">
            <i aria-hidden="true" class="fa fa-angle-left" style="font-size:2em;"></i>
            <span style="margin-left:5px;">
              {{ $t('pages.account.order.back') }}
            </span>
          </div>
        </a>
      </div>
  
      <div v-if="!success" style="padding:0;">
        <h4 class="n2-text-center">{{ $t('pages.account.profile.profile') }}</h4>
        <div style="max-width:600px;">
          <form-input type="text" v-model="contact.given_name"
            :label="$t('pages.account.profile.given_name')"
            :errors="model_errors.given_name" />
  
          <form-input type="text" v-model="contact.family_name"
            :label="$t('pages.account.profile.family_name')"
            :errors="model_errors.family_name" />
  
          <form-input type="text" v-model="contact.address.address_line_1"
            :label="$t('pages.account.profile.address_line_1')"
            :errors="model_errors.address_line_1" />
  
          <form-input type="text" v-model="contact.address.postal_code"
            :label="$t('pages.account.profile.postal_code')"
            :errors="model_errors.postal_code" />
  
          <form-input type="text" v-model="contact.address.locality"
            :label="$t('pages.account.profile.locality')"
            :errors="model_errors.locality" />
  
          <div class="n2-form-group">
            <label class="n2-form-label">
              {{ $t('pages.account.profile.country_code') }}
            </label>
            <app-autocomplete
              v-model="contact.address.country_code"
              :items="countries"
              :text_field="'name'"
              :separator="' '"
              :value_field="'code'"
              :errors="model_errors.country_code">
            </app-autocomplete>
          </div>
  
          <!-- Phone number field (E.164 v-model) -->
          <label class="n2-form-label">{{ $t('pages.account.profile.phone_number') }}</label>
          <phone-number-field
            v-model="contact.phone_number"                
            :defaultCountry="contact.address.country_code || 'NO'"
            :locale="app_locale"
            :errors="{ national: model_errors.phone_number }"
          />
  
          <form-input type="text" v-model="contact.email"
            :label="$t('pages.account.profile.email')"
            :errors="model_errors.email" />
  
          <form-input type="text" v-model="contact.email_repeat"
            :label="$t('pages.account.profile.email_repeat')"
            :errors="model_errors.email_repeat" />
  
          <validation-summary :errors="model_errors" />
  
          <div class="n2-margin-top">
            <button class="n2-button-1" @click="update">
              {{ $t('pages.account.profile.update') }}
            </button>
          </div>
        </div>
      </div>
  
      <div v-else class="n2-text-center" style="padding:0">
        <h4>{{ $t('pages.account.profile.update_success') }}</h4>
      </div>
  
      <app-loader :active="loading" />
    </div>
  </template>
  
  <script>
  import { mapGetters } from 'vuex';
  import { AccountService } from '../services'
  
  import FormInput from "../components/shared/form-input.vue"
  import ValidationSummary from '../components/shared/validation-summary.vue';
  import AppAutocomplete from '../components/shared/autocomplete.vue'
  import AppLoader from '../components/shared/loader.vue'
  import PhoneNumberField from '../components/app/phone-number-field.vue'
  
  import country_list from '../data/country-list'
  
  export default {
    components: { FormInput, ValidationSummary, AppAutocomplete, AppLoader, PhoneNumberField },
    data () {
      return {
        loading: false,
        model_errors: {},
        success: false,
        contact: {
          given_name: '',
          family_name: '',
          email: '',
          email_repeat: '',
          phone_number: '',     // E.164（由 PhoneNumberField 维护）
          address: {
            address_line_1: '',
            postal_code: '',
            locality: '',
            country_code: 'NO'
          }
        }
      };
    },
    computed: {
      ...mapGetters('globalization', ['app_locale','locale']),
      countries () {
        return this.app_locale === 'no' ? country_list.no : country_list.en
      }
    },
    created () {
      this.accountService = new AccountService()
    },
    mounted: async function () {
      await this.load();
    },
    methods: {
      validateForm () {
        this.model_errors = {}
  
        if (!this.contact.given_name) {
          this.model_errors['given_name'] = [this.$t('pages.account.profile.error_given_name')]
        }
        if (!this.contact.family_name) {
          this.model_errors['family_name'] = [this.$t('pages.account.profile.error_family_name')]
        }
        if (!this.contact.address.address_line_1) {
          this.model_errors['address_line_1'] = [this.$t('pages.account.profile.error_address_line_1')]
        }
        if (!this.contact.address.postal_code) {
          this.model_errors['postal_code'] = [this.$t('pages.account.profile.error_postal_code')]
        }
        if (!this.contact.address.locality) {
          this.model_errors['locality'] = [this.$t('pages.account.profile.error_locality')]
        }
        if (!this.contact.address.country_code) {
          this.model_errors['country_code'] = [this.$t('pages.account.profile.error_country_code')]
        }
  
        // Phone：只需校验“有值”，格式由 PhoneNumberField 控制且 v-model 仅输出合法 E.164
        if (!this.contact.phone_number) {
          this.model_errors['phone_number'] = [this.$t('pages.account.profile.error_phone_number')]
        }
  
        const patternEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!patternEmail.test(this.contact.email || '')) {
          this.model_errors['email'] = [this.$t('pages.account.profile.error_email')]
        }
        if ((this.contact.email || '').toLowerCase().trim() !== (this.contact.email_repeat || '').toLowerCase().trim()) {
          this.model_errors['email_repeat'] = [this.$t('pages.account.profile.error_email_repeat')]
        }
  
        return Object.keys(this.model_errors).length === 0
      },
      async load () {
        this.contact = await this.accountService.getDetails();
        this.contact.email_repeat = this.contact.email;
        // 确保 phone_number 字段存在（E.164），便于 PhoneNumberField 反解析显示
        if (typeof this.contact.phone_number !== 'string') {
          this.contact.phone_number = ''
        }
      },
      async update () {
        if (!this.validateForm()) return
        try {
          this.loading = true;
          this.contact.email = (this.contact.email || '').toLowerCase().trim();
          // ⚠️ 不要对手机号做 toLowerCase；仅在组件内部已标准化为 E.164
          await this.accountService.update(this.contact)
          this.success = true
        }
        catch (err) {

          if (err && err.data) {
            let errorCode = err?.data?.error_code;
            if (errorCode) {
                if(errorCode == 'model_error')
                {
                    if(err.data.errors.phone_number){
                    this.model_errors['phone_number'] = [this.$t('pages.account.profile.error_phone_number')]
                }}
            }

            if (err.data.errors.email) {
              this.$set(this.model_errors, 'email', [this.$t('pages.account.profile.error_email_exist')])
            }
          }
        }
        finally {
          this.loading = false;
        }
      }
    }
  }
  </script>
  
  <style lang="sass">
  </style>